import { Download, Check, Container, Coffee } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const SCANNER_VERSION = '1.0.0';
const DOWNLOADS_BASE_URL = '/downloads';
const INSTALLER_FILENAME = `ORbiT8-Scanner-Setup-${SCANNER_VERSION}.exe`;
const INSTALLER_SIZE = '69 MB';
const ZIP_FILENAME = `ORbiT8-Scanner-${SCANNER_VERSION}.zip`;
const ZIP_SIZE = '104 MB';

const INSTALLER_CHECKLIST = [
  'Per-user or machine-wide install',
  'Start Menu & Desktop shortcuts',
  'Uninstall via Windows Settings › Apps',
];

const ZIP_CHECKLIST = [
  'No installation required',
  'Run from any writable folder',
  'No registry changes or shortcuts',
];

export function DownloadScanner() {
  return (
    <div className="min-h-screen bg-background text-foreground py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto bg-card p-8 rounded-xl shadow-lg border border-border">

        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-wider text-primary mb-2">
            Windows 64-bit &middot; v{SCANNER_VERSION}
          </p>
          <h1 className="text-3xl font-bold text-foreground">Download ORbiT8 Scanner</h1>
          <p className="mt-2 text-muted-foreground max-w-xl">
            Choose the package that fits your environment.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <Card className="border-primary shadow-md">
            <CardContent className="p-6 flex flex-col h-full">
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Installer</span>
                <span className="text-xs font-mono uppercase tracking-wider text-primary-foreground bg-primary px-2.5 py-1 rounded-full">
                  Recommended
                </span>
              </div>
              <h2 className="text-lg font-bold text-foreground mb-2">Setup Wizard</h2>
              <span className="inline-flex items-baseline gap-2 font-mono text-xs text-muted-foreground bg-muted border border-border rounded px-2.5 py-1 mb-4 w-fit break-all">
                {INSTALLER_FILENAME}
                <span className="text-muted-foreground/70">{INSTALLER_SIZE}</span>
              </span>
              <p className="text-sm text-muted-foreground mb-4">
                A guided installation wizard that walks you through setup in a few clicks. Choose to install for your account only or for everyone on the machine.
              </p>
              <ul className="flex flex-col gap-2 mb-6 flex-1">
                {INSTALLER_CHECKLIST.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href={`${DOWNLOADS_BASE_URL}/${INSTALLER_FILENAME}`} download>
                <Button className="w-full flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Download Installer
                </Button>
              </a>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 flex flex-col h-full">
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Portable</span>
              </div>
              <h2 className="text-lg font-bold text-foreground mb-2">ZIP Archive</h2>
              <span className="inline-flex items-baseline gap-2 font-mono text-xs text-muted-foreground bg-muted border border-border rounded px-2.5 py-1 mb-4 w-fit break-all">
                {ZIP_FILENAME}
                <span className="text-muted-foreground/70">{ZIP_SIZE}</span>
              </span>
              <p className="text-sm text-muted-foreground mb-4">
                Extract and run with no installation step. Nothing is written to the registry and no shortcuts are created, making it suitable for evaluation, IT testing, or environments where software installation is restricted.
              </p>
              <ul className="flex flex-col gap-2 mb-6 flex-1">
                {ZIP_CHECKLIST.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href={`${DOWNLOADS_BASE_URL}/${ZIP_FILENAME}`} download>
                <Button variant="outline" className="w-full flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Download ZIP
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-muted-foreground">
                Prerequisites
              </h3>
            </div>
            <div className="p-6 flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
                <span className="text-xs font-mono font-semibold uppercase tracking-wide text-muted-foreground">
                  Operating System
                </span>
                <span className="text-sm text-muted-foreground">
                  <strong className="text-foreground font-semibold">Windows 10 or later</strong>, 64-bit. Required for both packages.
                </span>
              </div>

              <div className="h-px bg-border" />

              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
                <span className="text-xs font-mono font-semibold uppercase tracking-wide text-muted-foreground">
                  Scan Engine
                </span>
                <div className="text-sm text-muted-foreground">
                  Choose one of the following before running your first scan. The app will guide you through setup on first launch.
                  <div className="flex flex-col gap-2 mt-3">
                    <div className="flex items-start gap-3 bg-primary/5 border border-primary/30 rounded-lg px-3.5 py-2.5">
                      <Container className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          Docker Desktop 4.0 or later
                          <span className="text-[10px] font-mono uppercase tracking-wide text-primary-foreground bg-primary px-2 py-0.5 rounded-full">
                            Recommended
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Runs ORT inside a container with all package-manager tools bundled — npm, Go, Maven, .NET SDK, Python, and more. No additional software required on your machine.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 bg-muted/40 border border-border rounded-lg px-3.5 py-2.5">
                      <Coffee className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <div className="text-sm font-semibold text-foreground">Java 21 or later</div>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Runs ORT as a native binary (JAR). Each project's own package managers (npm, Go, Maven, .NET SDK, etc.) must also be present on your machine or needs to be downloaded.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
